import { Fragment } from 'react'
import {
  Flex,
  Box,
  Card,
  Heading,
  Text,
  colors,
} from '@weareberyl/design-system'

import { getVehicleTitle } from '~/components/ModuleHeader'
import { VehicleLink } from '~/components/Link'
import ZoneLink from '~/components/ZoneLink'
import LegendCard from './Card'
import { isDockedScheme } from '~/utils'

import ZoneTypeStatus from '~/components/Zone/ZoneTypeStatus'
import { Divider, message } from 'antd'
import {
  FrameType,
  Scheme,
  useZoneLegendQuery,
  ZoneLegendQuery_zone_Zone_all_commissioned_vehicles_Vehicle,
  ZoneType,
} from '~/gql/generated/graphql'
import AvailabilityIcon from '~/components/AvailabilityIcon'

interface IZoneVehicleLink {
  vehicle: ZoneLegendQuery_zone_Zone_all_commissioned_vehicles_Vehicle
  scheme: Pick<Scheme, 'id'>
}

const ZoneVehicleLink = ({ vehicle, scheme }: IZoneVehicleLink) => (
  <Box key={vehicle.id} mb={2}>
    <VehicleLink id={vehicle?.detail?.module?.id as string} bikeScheme={scheme}>
      <Card variant="gray" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <Flex justifyContent="space-between">
          <Text variant="small">
            {getVehicleTitle(
              vehicle.frame_id ?? null,
              vehicle.frame_type as FrameType,
              vehicle.detail?.module?.id,
            )}
          </Text>
          <AvailabilityIcon
            status={vehicle?.detail.module?.availability?.status}
          />
        </Flex>
      </Card>
    </VehicleLink>
  </Box>
)

export interface ZoneLegendProps {
  zone_id: string
  onClose: () => void
  where?: object | null
}

export const ZoneLegend = ({ zone_id, onClose }: ZoneLegendProps) => {
  const { loading, error, data } = useZoneLegendQuery({
    variables: {
      zone_id,
    },
    pollInterval: 0,
  })

  if (loading) return null

  if (error) {
    message.error(error.message)
  }

  const zone = data?.zone
  const showDockCount = zone && isDockedScheme(data?.zone?.scheme?.id)
  const modulesInZone = zone?.all_commissioned_modules_count ?? 0
  const zoneType = zone?.zone_type ?? ZoneType.normal

  const footer = {
    custom: modulesInZone ? (
      <Fragment>
        <Heading variant="h4" mt={2} mb={3}>
          Vehicles in Bay
        </Heading>
        {zone?.all_commissioned_vehicles?.map(v => (
          <ZoneVehicleLink key={v.id} vehicle={v} scheme={zone.scheme} />
        ))}
      </Fragment>
    ) : null,
  }

  return (
    <LegendCard onClose={onClose} footer={footer}>
      <Box flex="1">
        <ZoneLink id={zone_id}>
          <Flex flexDirection="column" alignItems="center">
            <ZoneTypeStatus
              zoneType={zoneType}
              isSuspended={zone?.is_suspended}
              isPromotional={zone?.is_promotional}
              isPriority={zone?.is_priority}
            />
            <Heading
              color={colors.textLink}
              variant="h3"
              style={{ textAlign: 'center' }}
            >
              {zone?.name}
            </Heading>
            <Divider />
          </Flex>
        </ZoneLink>
        <Flex justifyContent="space-between" width="100%" mb={3}>
          <Flex flex="1" mr={3}>
            <Text variant="small">Total: </Text>
            <Text variant="small" fontFamily="Hellix-SemiBold">
              {zone?.all_commissioned_modules_count}
            </Text>
          </Flex>
          <Flex flex="1">
            <Text variant="small">Spaces: </Text>
            <Text variant="small" fontFamily="Hellix-SemiBold">
              {zone?.capacity}
            </Text>
          </Flex>
        </Flex>
        {showDockCount && (
          <Flex justifyContent="space-between" width="100%" mb={3}>
            <Flex flex="1" mr={3}>
              <Text variant="small">Docked: </Text>
              <Text variant="small" fontFamily="Hellix-SemiBold">
                {zone?.docked_vehicles_count}
              </Text>
            </Flex>
          </Flex>
        )}
        <Flex justifyContent="space-between" width="100%" mb={3}>
          <Flex flex="1" mr={3}>
            <Text variant="small">Bikes: </Text>
            <Text variant="small" fontFamily="Hellix-SemiBold">
              {/* `bikes` includes ebikes (they both have propulsion_type: human), deduct them to show pedal bikes */}
              {(zone?.bikes ?? 0) - (zone?.ebikes ?? 0)}
            </Text>
          </Flex>
          <Flex flex="1">
            <Text variant="small">e-Bikes: </Text>
            <Text variant="small" fontFamily="Hellix-SemiBold">
              {zone?.ebikes}
            </Text>
          </Flex>
        </Flex>
        <Flex width="100%" mb={2}>
          <Flex flex="1" mr={3}>
            <Text variant="small">e-Scooters: </Text>
            <Text variant="small" fontFamily="Hellix-SemiBold">
              {zone?.scooters}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </LegendCard>
  )
}

export default ZoneLegend
